import React, { useCallback, useMemo } from 'react';
import { useObservable } from 'react-use-observable';

import { LockOutlined, RightOutlined } from '@ant-design/icons';
import { Avatar, Skeleton, Tag, Tooltip } from 'antd';

import clsx from 'clsx';
import Footer from 'v2/components/Shared/Footer';
import type { IAccess } from 'v2/interfaces/account';
import eduzzAccountsService from 'v2/services/eduzzAccount';

import Header from './Header';

function AccountRow({ access, onSelect }: { access: IAccess; onSelect: (a: IAccess) => void }) {
  const disabled = !access.termsAcceptance && !access.isMe;

  const content = (
    <div
      className={clsx(
        'border-border flex cursor-pointer items-center gap-4 border-b p-4 transition-[background-color,transform] duration-[120ms,60ms] ease-[ease,ease] hover:bg-black/4 active:translate-y-px',
        {
          'pointer-events-none opacity-50': disabled
        }
      )}
      onClick={() => !disabled && onSelect(access)}
      role='button'
      aria-disabled={disabled}
      aria-label={`Entrar como ${access.name} (${access.email})`}
    >
      <div>
        <Avatar size={50} src={access.photo} className='border-border border'>
          {access.name?.at(0)}
        </Avatar>
      </div>

      <div className='flex flex-1 flex-col items-start'>
        <div className='flex flex-row gap-3'>
          <span className='text-base font-semibold'>{access.name}</span>
          {access.isMe && (
            <Tag className='bg-primary! border-primary! text-content-negative! leading-5.5!'>Minha conta</Tag>
          )}
          {disabled && <Tag icon={<LockOutlined style={{ fontSize: 14 }} />}>Aguardando aceite</Tag>}
        </div>
        <span className={`text-sm text-gray-500 ${disabled ? 'opacity-70' : ''}`}>{access.email}</span>
      </div>

      <RightOutlined />
    </div>
  );

  if (!disabled) return content;

  return (
    <Tooltip
      title={
        <span className='text-sm'>
          O titular desta conta ainda não aceitou a atualização dos nossos Termos de Uso. Solicite a confirmação ao
          responsável; após o aceite, seu acesso será liberado. Dúvidas: suporte@eduzz.com
        </span>
      }
      placement='left'
    >
      <div>{content}</div>
    </Tooltip>
  );
}

export default function SelectAccount() {
  const [accesses] = useObservable(() => eduzzAccountsService.getAccesses(), []);
  const accounts = Array.isArray(accesses) ? accesses : [];

  const onChange = useCallback((a: IAccess) => {
    eduzzAccountsService.changeAccount(a.email);
  }, []);

  const sorted = useMemo(() => {
    if (!accesses) return [];
    const copy = [...accesses];
    copy.sort((a, b) => (a.isMe === b.isMe ? a.name.localeCompare(b.name) : a.isMe ? -1 : 1));
    return copy;
  }, [accesses]);

  const isLoading = !accounts.length;

  return (
    <div>
      <div className='eduzz-ui-layout-topbar'>
        <Header />
      </div>
      <main className='bg-grey-50 flex min-h-[80vh] flex-col items-center justify-center p-6 text-center'>
        <div>
          <h2 className='text-xl font-bold'>Escolha uma conta</h2>
          <p className='text-base text-gray-500'>
            {isLoading ? 'Carregando contas...' : 'Você pode alternar a qualquer momento no menu superior'}
          </p>
        </div>
        <div className='border-border mt-6 mb-8 w-full max-w-[520px] border bg-white p-0' aria-label='Lista de contas'>
          {isLoading
            ? [1, 2, 3].map(i => (
                <div key={i} className='border-border flex items-center border-b p-4'>
                  <Skeleton.Avatar active size={40} className='mr-4' />
                  <div className='flex-1'>
                    <Skeleton.Input active size='small' style={{ width: '40%' }} />
                    <Skeleton.Input active size='small' style={{ width: '60%', marginTop: 4 }} />
                  </div>
                </div>
              ))
            : sorted.map(access => <AccountRow key={access.id} access={access} onSelect={onChange} />)}
        </div>
        <Footer hideQuickHelp style={{ margin: 0, alignSelf: 'end' }} />
      </main>
    </div>
  );
}
